import {
  estimateStripeFeeMinor,
  getPlatformFeeMinor,
  getStripeFeeConfig,
  grossUpForStripeFeeMinor,
} from '../../../utils/fees';
import { RESTRICTABLE_DASHBOARD_CARDS, normalizeRestrictedCardIds } from './moduleRestrictions';

export const LEARN_TO_SAIL_GLOBAL_TAG = 'Learn to Sail';

export function normalizeLearnToSailFeeHandling(value, fallback = 'add') {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === 'add' || normalized === 'deduct') return normalized;
  return fallback === 'deduct' ? 'deduct' : 'add';
}

export function getLearnToSailGeneralSettings(config = {}) {
  const restrictActiveCourseCards = config?.restrictActiveCourseCards === true;
  const configuredVisibleCardIds = Array.isArray(config?.activeCourseVisibleCardIds)
    ? config.activeCourseVisibleCardIds
    : (restrictActiveCourseCards ? RESTRICTABLE_DASHBOARD_CARDS.map((card) => card.id) : []);
  return {
    waitlistOpen: config?.waitlistOpen === true,
    memberNestFeeHandling: normalizeLearnToSailFeeHandling(config?.memberNestFeeHandling, 'add'),
    stripeFeeHandling: normalizeLearnToSailFeeHandling(config?.stripeFeeHandling, 'add'),
    restrictActiveCourseCards,
    activeCourseVisibleCardIds: normalizeRestrictedCardIds(configuredVisibleCardIds),
  };
}

export function getLearnToSailCourseFeeSettings(course = {}, moduleConfig = {}) {
  const courseFeeMinor = Math.max(0, Math.round(Number(course?.courseFeeMinor || 0)));
  const moduleSettings = getLearnToSailGeneralSettings(moduleConfig);
  const missingFallback = courseFeeMinor <= 0 ? 'deduct' : null;
  return {
    memberNestFeeHandling: normalizeLearnToSailFeeHandling(
      course?.memberNestFeeHandling,
      missingFallback || moduleSettings.memberNestFeeHandling
    ),
    stripeFeeHandling: normalizeLearnToSailFeeHandling(
      course?.stripeFeeHandling,
      missingFallback || moduleSettings.stripeFeeHandling
    ),
  };
}

export function calculateLearnToSailCheckoutPreview({
  courseFeeMinor = 0,
  currency = 'GBP',
  teamFeeCfg = {},
  learnToSailModule = {},
  course = {},
} = {}) {
  const baseAmountMinor = Math.max(0, Math.round(Number(courseFeeMinor || 0)));
  const settings = getLearnToSailCourseFeeSettings(
    { ...course, courseFeeMinor: baseAmountMinor },
    learnToSailModule
  );
  const platformFeeMinor = Math.max(0, getPlatformFeeMinor({
    currency,
    teamFeeCfg,
  }));
  const stripeFeeConfig = getStripeFeeConfig({
    currency,
    teamFeeCfg,
  });
  const chargedPlatformFeeMinor = settings.memberNestFeeHandling === 'add' ? platformFeeMinor : 0;
  const subtotalBeforeStripeMinor = Math.max(0, baseAmountMinor + chargedPlatformFeeMinor);
  const totalDueMinor = settings.stripeFeeHandling === 'add'
    ? grossUpForStripeFeeMinor(subtotalBeforeStripeMinor, stripeFeeConfig)
    : subtotalBeforeStripeMinor;
  const stripeFeeMinor = settings.stripeFeeHandling === 'add'
    ? Math.max(0, totalDueMinor - subtotalBeforeStripeMinor)
    : Math.max(0, estimateStripeFeeMinor(totalDueMinor, stripeFeeConfig));
  return {
    baseAmountMinor,
    platformFeeMinor,
    chargedPlatformFeeMinor,
    stripeFeeMinor,
    chargedStripeFeeMinor: settings.stripeFeeHandling === 'add' ? stripeFeeMinor : 0,
    totalDueMinor,
    memberNestFeeHandling: settings.memberNestFeeHandling,
    stripeFeeHandling: settings.stripeFeeHandling,
  };
}

export function normalizeLearnToSailSignupMode(course = {}) {
  const raw = typeof course?.signupMode === 'string' ? course.signupMode.trim().toLowerCase() : '';
  if (raw === 'open_to_all' || raw === 'waitlist_only' || raw === 'closed') return raw;
  return course?.publicSignupEnabled === true ? 'open_to_all' : 'closed';
}

export function isLearnToSailCoursePubliclyActionable(course = {}, waitlistOpen = false) {
  if (course?.status === 'cancelled') return false;
  const signupMode = normalizeLearnToSailSignupMode(course);
  return signupMode === 'open_to_all' || (signupMode === 'waitlist_only' && waitlistOpen === true);
}

export function normalizeLearnToSailConversionStartMode(value, fallback = 'after_course_end') {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === 'after_course_end' || normalized === 'manual_date') return normalized;
  return fallback === 'manual_date' ? 'manual_date' : 'after_course_end';
}

export function getLearnToSailCourseTagName(courseName = '') {
  const normalizedName = typeof courseName === 'string' ? courseName.trim() : '';
  return normalizedName ? `L2S: ${normalizedName}` : 'L2S: Course';
}

export function isLearnToSailSystemTag(tag = {}) {
  return tag?.systemManaged === true && tag?.systemSource === 'learn_to_sail';
}

export function isEditableTeamTag(tag = {}) {
  return tag?.type !== 'ageRule' && !isLearnToSailSystemTag(tag);
}

export function filterEditableTeamTags(tags = []) {
  return (Array.isArray(tags) ? tags : []).filter((tag) => isEditableTeamTag(tag));
}

export function extractSystemManagedTags(tags = []) {
  return (Array.isArray(tags) ? tags : []).filter((tag) => tag?.systemManaged === true);
}

export function upsertLearnToSailSystemTags(tags = [], { courseId = '', courseName = '' } = {}) {
  const safeCourseId = typeof courseId === 'string' ? courseId.trim() : '';
  const next = (Array.isArray(tags) ? tags : []).filter((tag) => {
    if (!isLearnToSailSystemTag(tag)) return true;
    if (!safeCourseId) return false;
    return tag?.learnToSailScope !== 'course' || tag?.learnToSailCourseId !== safeCourseId;
  });

  const globalTag = {
    name: LEARN_TO_SAIL_GLOBAL_TAG,
    category: 'System',
    selfAssignable: false,
    multiAssignable: false,
    visibleToMembers: false,
    systemManaged: true,
    systemSource: 'learn_to_sail',
    learnToSailScope: 'global',
  };

  const courseTag = safeCourseId
    ? {
        name: getLearnToSailCourseTagName(courseName),
        category: 'System',
        selfAssignable: false,
        multiAssignable: false,
        visibleToMembers: false,
        systemManaged: true,
        systemSource: 'learn_to_sail',
        learnToSailScope: 'course',
        learnToSailCourseId: safeCourseId,
      }
    : null;

  const deduped = [];
  const seen = new Set();
  [...next, globalTag, ...(courseTag ? [courseTag] : [])].forEach((tag) => {
    const key = `${tag?.name || ''}::${tag?.learnToSailCourseId || ''}::${tag?.learnToSailScope || ''}`;
    if (!tag?.name || seen.has(key)) return;
    seen.add(key);
    deduped.push(tag);
  });

  return deduped;
}

export function createLearnToSailSession(input = {}) {
  const id =
    typeof input?.id === 'string' && input.id.trim()
      ? input.id.trim()
      : (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `lts_session_${Date.now()}`);
  return {
    id,
    date: typeof input?.date === 'string' ? input.date : '',
    startTime: typeof input?.startTime === 'string' ? input.startTime : '',
    endTime: typeof input?.endTime === 'string' ? input.endTime : '',
    eventId: typeof input?.eventId === 'string' ? input.eventId : '',
    status: input?.status === 'cancelled' ? 'cancelled' : 'scheduled',
    assetReservations: Array.isArray(input?.assetReservations)
      ? input.assetReservations.map((reservation) => ({ ...reservation }))
      : [],
  };
}

export function normalizeLearnToSailSessions(sessions = []) {
  return (Array.isArray(sessions) ? sessions : [])
    .map((session) => createLearnToSailSession(session))
    .filter((session) => session.date && session.startTime && session.endTime)
    .sort((left, right) => {
      const leftKey = `${left.date}T${left.startTime}`;
      const rightKey = `${right.date}T${right.startTime}`;
      return leftKey.localeCompare(rightKey);
    });
}

export function calculateLearnToSailFinalSessionEnd(sessions = []) {
  const normalized = normalizeLearnToSailSessions(sessions);
  if (normalized.length === 0) return null;
  const finalSession = normalized[normalized.length - 1];
  const parsed = new Date(`${finalSession.date}T${finalSession.endTime}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function padNumber(value) {
  return String(value).padStart(2, '0');
}

export function formatLearnToSailDateInputValue(value = '') {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getFullYear()}-${padNumber(parsed.getMonth() + 1)}-${padNumber(parsed.getDate())}`;
}

export function parseLearnToSailLocalDateAtMidnight(value = '') {
  const formatted = formatLearnToSailDateInputValue(value);
  if (!formatted) return null;
  const parsed = new Date(`${formatted}T00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function resolveLearnToSailConversionStartsAt(value = {}) {
  const primary = value?.conversionStartsAt || value?.finalSessionEndsAt || null;
  if (!primary) return null;
  if (typeof primary?.toDate === 'function') return primary.toDate();
  if (primary instanceof Date) return primary;
  const parsed = new Date(primary);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function calculateLearnToSailConversionStartsAt(
  sessions = [],
  {
    conversionStartMode = 'after_course_end',
    manualConversionStartAt = '',
  } = {}
) {
  const normalizedMode = normalizeLearnToSailConversionStartMode(conversionStartMode);
  if (normalizedMode === 'manual_date') {
    return parseLearnToSailLocalDateAtMidnight(manualConversionStartAt);
  }
  return calculateLearnToSailFinalSessionEnd(sessions);
}

export function calculateLearnToSailPurgeAt(
  sessions = [],
  postCoursePurgeDays = 0,
  options = {}
) {
  const conversionStartsAt = calculateLearnToSailConversionStartsAt(sessions, options);
  if (!(conversionStartsAt instanceof Date)) return null;
  const purgeAt = new Date(conversionStartsAt);
  purgeAt.setDate(purgeAt.getDate() + Math.max(1, Number(postCoursePurgeDays || 0)));
  return purgeAt;
}

export function buildLearnToSailEventDocId(courseId = '', sessionId = '') {
  const safeCourseId = typeof courseId === 'string' ? courseId.trim() : '';
  const safeSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
  return safeCourseId && safeSessionId ? `learn_to_sail__${safeCourseId}__${safeSessionId}` : '';
}

export function buildLearnToSailEventPayload({
  course = {},
  courseId = '',
  session = {},
  sessionIndex = 0,
} = {}) {
  const start = new Date(`${session.date}T${session.startTime}`);
  const end = new Date(`${session.date}T${session.endTime}`);
  const durationMinutes = Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));
  return {
    name: `${course.name || 'Learn to Sail'} · Session ${sessionIndex + 1}`,
    description: course.description || '',
    type: 'oneOff',
    date: session.date,
    time: session.startTime,
    start,
    end,
    durationMinutes,
    attendanceMode: 'rsvp',
    capacity: Math.max(1, Number(course.capacity || 1)),
    allowNonMembers: false,
    eventMode: 'inPerson',
    locationName: typeof course.locationName === 'string' ? course.locationName.trim() : '',
    locationStreet: typeof course.locationStreet === 'string' ? course.locationStreet.trim() : '',
    locationCity: typeof course.locationCity === 'string' ? course.locationCity.trim() : '',
    locationPostcode: typeof course.locationPostcode === 'string' ? course.locationPostcode.trim() : '',
    locationCountry: typeof course.locationCountry === 'string' ? course.locationCountry.trim() : '',
    tags: [LEARN_TO_SAIL_GLOBAL_TAG, getLearnToSailCourseTagName(course.name)].filter(Boolean),
    sourceType: 'learn_to_sail_course',
    learnToSailCourseId: courseId || null,
    learnToSailSessionId: session.id || null,
    learnToSailGenerated: true,
    status: session.status === 'cancelled' || course.status === 'cancelled' ? 'cancelled' : 'scheduled',
    updatedAt: new Date().toISOString(),
  };
}
